export const STEP_ROUTE_SEGMENTS = {
  3.5: 'plan',
  4: 'results',
  5: 'edit',
  5.5: 'pom',
  5.6: 'mobile-pom',
  6: 'save',
  7: 'scripts',
  7.5: 'mobile-scripts',
  8: 'execution',
};

const ROUTE_SEGMENT_STEPS = Object.entries(STEP_ROUTE_SEGMENTS).reduce(
  (entries, [step, segment]) => ({
    ...entries,
    [segment]: Number(step),
  }),
  {}
);

export const normalizePdfWorkflowStep = (step, fallbackStep = 5) => {
  const parsed = Number(step);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallbackStep;
  return parsed;
};

export const parsePositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const decodePathPart = (value) => {
  try {
    return decodeURIComponent(value || '');
  } catch (_err) {
    return value || '';
  }
};

const normalizeWorkflowRouteRoot = (value = 'pdf') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'requirements') return 'requirements';
  if (normalized === 'api-testing') return 'api-testing';
  if (normalized === 'excel-testcases') return 'excel-testcases';
  return 'pdf';
};

const APPLICATION_ROUTE_MODULES = {
  pdf: 'brd',
  requirements: 'requirements',
  'api-testing': 'api',
  'excel-testcases': 'excel',
};

const parseWorkflowTail = (parts, { applicationId = '', isApplicationRoute = false } = {}) => {
  const pdfId = String(parts[0] || '').trim();
  if (!pdfId || pdfId === 'new') {
    return {
      isPdfRoute: true,
      isApplicationRoute,
      applicationId,
      pdfId: '',
      importId: '',
      sectionNumber: null,
      bulkJobId: '',
      step: 1,
    };
  }

  if (pdfId === 'import') {
    return {
      isPdfRoute: true,
      isApplicationRoute,
      applicationId,
      pdfId: '',
      importId: String(parts[1] || '').trim(),
      sectionNumber: null,
      bulkJobId: '',
      step: 1.5,
    };
  }

  if (parts[1] === 'generation') {
    return {
      isPdfRoute: true,
      isApplicationRoute,
      applicationId,
      pdfId,
      importId: '',
      sectionNumber: null,
      bulkJobId: String(parts[2] || '').trim(),
      step: 4,
    };
  }

  if (parts[1] !== 'sections') {
    return {
      isPdfRoute: true,
      isApplicationRoute,
      applicationId,
      pdfId,
      importId: '',
      sectionNumber: null,
      bulkJobId: '',
      step: 2,
    };
  }

  const sectionNumber = parsePositiveNumber(parts[2]);
  if (!sectionNumber) {
    return {
      isPdfRoute: true,
      isApplicationRoute,
      applicationId,
      pdfId,
      importId: '',
      sectionNumber: null,
      bulkJobId: '',
      step: 3,
    };
  }

  const segment = String(parts[3] || '').trim().toLowerCase();
  const segmentStep = ROUTE_SEGMENT_STEPS[segment];
  return {
    isPdfRoute: true,
    isApplicationRoute,
    applicationId,
    pdfId,
    importId: '',
    sectionNumber,
    bulkJobId: '',
    step: normalizePdfWorkflowStep(segmentStep, 5),
  };
};

export const normalizePdfWorkflowPath = (
  pathname = '',
  routeRoot = 'pdf',
  expectedApplicationId = ''
) => {
  const normalizedRouteRoot = normalizeWorkflowRouteRoot(routeRoot);
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)
    .map(decodePathPart);

  const applicationModule = APPLICATION_ROUTE_MODULES[normalizedRouteRoot];
  if (parts[0] === 'applications' && parts[2] === applicationModule) {
    const applicationId = String(parts[1] || '').trim();
    const expectedId = String(expectedApplicationId || '').trim();
    if (expectedId && applicationId !== expectedId) {
      return {
        isPdfRoute: false,
        isApplicationRoute: true,
        applicationId,
        pdfId: '',
        importId: '',
        sectionNumber: null,
        bulkJobId: '',
        step: 1,
      };
    }
    return parseWorkflowTail(parts.slice(3), {
      applicationId,
      isApplicationRoute: true,
    });
  }

  if (parts[0] !== normalizedRouteRoot) {
    return {
      isPdfRoute: false,
      isApplicationRoute: false,
      applicationId: '',
      pdfId: '',
      importId: '',
      sectionNumber: null,
      bulkJobId: '',
      step: 1,
    };
  }

  return parseWorkflowTail(parts.slice(1));
};

export const buildPdfWorkflowPath = ({
  pdfId,
  sectionNumber,
  step,
  bulkJobId,
  importId,
  routeRoot = 'pdf',
  applicationId = '',
}) => {
  const normalizedRouteRoot = normalizeWorkflowRouteRoot(routeRoot);
  const normalizedApplicationId = String(applicationId || '').trim();
  const applicationModule = APPLICATION_ROUTE_MODULES[normalizedRouteRoot];
  const routePrefix = normalizedApplicationId
    ? `/applications/${encodeURIComponent(normalizedApplicationId)}/${applicationModule}`
    : `/${normalizedRouteRoot}`;
  const numericStep = normalizePdfWorkflowStep(step, 5);
  const normalizedImportId = String(importId || '').trim();
  if (numericStep === 1.5 && normalizedImportId) {
    return `${routePrefix}/import/${encodeURIComponent(normalizedImportId)}`;
  }

  const normalizedPdfId = String(pdfId || '').trim();
  if (!normalizedPdfId) return normalizedApplicationId ? `${routePrefix}/new` : routePrefix;

  const encodedPdfId = encodeURIComponent(normalizedPdfId);
  if (numericStep <= 2) return `${routePrefix}/${encodedPdfId}`;
  if (numericStep === 4 && !parsePositiveNumber(sectionNumber)) {
    const normalizedBulkJobId = String(bulkJobId || '').trim();
    return normalizedBulkJobId
      ? `${routePrefix}/${encodedPdfId}/generation/${encodeURIComponent(normalizedBulkJobId)}`
      : `${routePrefix}/${encodedPdfId}/generation`;
  }
  if (numericStep === 3) return `${routePrefix}/${encodedPdfId}/sections`;

  const normalizedSection = parsePositiveNumber(sectionNumber);
  if (!normalizedSection) return `${routePrefix}/${encodedPdfId}/sections`;

  const segment = STEP_ROUTE_SEGMENTS[numericStep] || STEP_ROUTE_SEGMENTS[4];
  return `${routePrefix}/${encodedPdfId}/sections/${normalizedSection}/${segment}`;
};
